import { intersectRect, invertMatrix, multiplyMatrix, normalizeRect, transformPoint, transformRectBounds, type Matrix, type Point, type Rect } from './geometry';
import {
  createGroupLayer,
  createId,
  findLayer,
  findLayerParent,
  layerDocumentTransform,
  layerRetainedBytes,
  validateDimensions,
  type EditorDocument,
  type Guide,
  type GroupLayer,
  type Layer,
  type LayerBase,
  type LayerId,
  type RasterLayer,
} from './model';
import { combineRectangle, combineRegions, pointIsSelected, selectionBounds, type SelectionCombineMode, type SelectionState } from './selection';
import { RasterTileStore } from './tiles';
import { EMPTY_DAMAGE, damage, mergeDamage, type DamageSet } from './damage';
import type { ActionStep } from './action-types';
import type { SolidPaint, VectorObject, VectorStroke } from '../vector/types';

export type CommandExecution =
  | { outcome: 'changed'; damage: DamageSet }
  | { outcome: 'no-op'; damage: DamageSet }
  | { outcome: 'rejected'; damage: DamageSet; reason: string };

export function changed(commandDamage: DamageSet = EMPTY_DAMAGE): CommandExecution {
  return { outcome: 'changed', damage: commandDamage };
}

export function noOp(): CommandExecution {
  return { outcome: 'no-op', damage: EMPTY_DAMAGE };
}

export function rejected(reason: string): CommandExecution {
  return { outcome: 'rejected', damage: EMPTY_DAMAGE, reason };
}

export interface Command {
  /** Release resources owned by this history entry; implementations must be idempotent. */
  dispose?(): void;
  readonly label: string;
  retainedBytes(): number;
  execute(document: EditorDocument): CommandExecution;
  undo(document: EditorDocument): void;
  redo?(document: EditorDocument): void;
  damage?(): DamageSet;
  toActionStep?(): ActionStep | null;
}

export interface LiveCommand extends Command {
  seal(document: EditorDocument): CommandExecution;
}

export interface MatrixTransformCommand extends LiveCommand {
  update(document: EditorDocument, transform: Matrix): void;
}

export function isMatrixTransformCommand(command: LiveCommand): command is MatrixTransformCommand {
  return typeof (command as Partial<MatrixTransformCommand>).update === 'function';
}

export interface HistoryState {
  canUndo: boolean;
  canRedo: boolean;
  undoLabel?: string;
  redoLabel?: string;
  entries: readonly string[];
  timeline: readonly string[];
  cursor: number;
  retainedBytes: number;
  revision: number;
}

function touch(document: EditorDocument): void {
  document.updatedAt = new Date().toISOString();
}

/**
 * Who, other than the user at the keyboard, caused a commit made through the
 * raw history API. Semantic transactions carry their actor themselves; this
 * covers native commands executed on behalf of an add-on or an action replay.
 */
export type HistoryOrigin = Readonly<{ kind: 'addon'; id: string } | { kind: 'replay'; action: string }>;

export interface HistoryEvent {
  revision: number;
  damage: DamageSet;
  kind: 'commit' | 'undo' | 'redo' | 'preview' | 'cancel' | 'reset';
  /** The history entry that was committed, undone or redone. Absent for preview, cancel and reset. */
  command?: Command;
  /** Set on commits made inside withOrigin(); absent means the user. */
  origin?: HistoryOrigin;
}

/** Why a command left history memory. `budget` is byte-budget eviction of the oldest undo entry. */
export type HistoryEvictionReason = 'budget' | 'discarded' | 'cleared';

/** Read-only description of the active live gesture, for callers that must not interrupt it. */
export interface LiveCommandInfo {
  label: string;
  damage: DamageSet;
}

function normalizedExecution(command: Command, result: CommandExecution): CommandExecution {
  return result.outcome === 'changed' && result.damage === EMPTY_DAMAGE && command.damage
    ? changed(command.damage())
    : result;
}

export function retainedCommandBytes(command: Command): number {
  const measured = command.retainedBytes();
  return Number.isFinite(measured) && measured > 0 ? Math.ceil(measured) : 0;
}

export class HistoryManager {
  private past: Command[] = [];
  private future: Command[] = [];
  private live: LiveCommand | null = null;
  private liveDamage: DamageSet = EMPTY_DAMAGE;
  private revision = 0;
  private listeners = new Set<(event: HistoryEvent) => void>();
  private commandListeners = new Set<(command: Command) => void>();
  private evictionListeners = new Set<(command: Command, reason: HistoryEvictionReason) => void>();
  private origin: HistoryOrigin | undefined;

  constructor(private readonly byteBudget = 256 * 1024 * 1024) {}

  /**
   * Runs `run` synchronously with every commit it makes attributed to
   * `origin`. Only synchronous work is covered, so a user edit made while an
   * asynchronous caller awaits is never misattributed.
   */
  withOrigin<T>(origin: HistoryOrigin, run: () => T): T {
    const previous = this.origin;
    this.origin = origin;
    try { return run(); } finally { this.origin = previous; }
  }

  /** Entries in timeline order: undoable entries oldest first, then redoable entries next-to-redo first (parallel to state().timeline). */
  timelineCommands(): readonly Command[] {
    return [...this.past, ...[...this.future].reverse()];
  }

  get hasLiveCommand(): boolean { return this.live !== null; }

  /** Label and accumulated damage of the active live gesture, or null when none is active. */
  liveCommandInfo(): LiveCommandInfo | null {
    return this.live ? { label: this.live.label, damage: mergeDamage(this.liveDamage, this.live.damage?.() ?? EMPTY_DAMAGE) } : null;
  }

  /** The most recent undoable entry. */
  top(): Command | undefined { return this.past.at(-1); }

  /** The entry redo would restore next. */
  nextRedo(): Command | undefined { return this.future.at(-1); }

  /** Finds an entry, searching undoable entries newest first, then redoable entries next-to-redo first. */
  find(predicate: (command: Command) => boolean): { command: Command; stack: 'past' | 'future'; position: number } | undefined {
    for (let index = this.past.length - 1; index >= 0; index -= 1) {
      const command = this.past[index];
      if (command && predicate(command)) return { command, stack: 'past', position: index + 1 };
    }
    for (let index = this.future.length - 1; index >= 0; index -= 1) {
      const command = this.future[index];
      if (command && predicate(command)) return { command, stack: 'future', position: this.past.length + (this.future.length - index) };
    }
    return undefined;
  }

  /** Called just before a history entry is disposed because it left memory. */
  subscribeEvictions(listener: (command: Command, reason: HistoryEvictionReason) => void): () => void {
    this.evictionListeners.add(listener);
    return () => this.evictionListeners.delete(listener);
  }

  private evict(command: Command, reason: HistoryEvictionReason): void {
    for (const listener of this.evictionListeners) listener(command, reason);
    command.dispose?.();
  }

  subscribe(listener: (event: HistoryEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribeCommands(listener: (command: Command) => void): () => void {
    this.commandListeners.add(listener);
    return () => this.commandListeners.delete(listener);
  }

  private emit(kind: HistoryEvent['kind'], eventDamage: DamageSet = EMPTY_DAMAGE, command?: Command): void {
    const event: HistoryEvent = command ? { revision: this.revision, damage: eventDamage, kind, command } : { revision: this.revision, damage: eventDamage, kind };
    if (kind === 'commit' && this.origin) event.origin = this.origin;
    for (const listener of this.listeners) listener(event);
  }

  execute(document: EditorDocument, command: Command): CommandExecution {
    if (this.live) throw new Error('Cannot execute a command while a live command is active.');
    let result: CommandExecution;
    try { result = normalizedExecution(command, command.execute(document)); }
    catch (error) { command.dispose?.(); throw error; }
    if (result.outcome !== 'changed') { command.dispose?.(); return result; }
    touch(document);
    this.past.push(command);
    for (const discarded of this.future) this.evict(discarded, 'discarded');
    this.future = [];
    this.revision += 1;
    this.enforceBudget();
    this.emit('commit', result.damage, command);
    for (const listener of this.commandListeners) listener(command);
    return result;
  }

  applyTransient(document: EditorDocument, command: Command): CommandExecution {
    if (this.live) this.commitLive(document);
    try {
      const result = normalizedExecution(command, command.execute(document));
      if (result.outcome === 'changed') this.emit('preview', result.damage);
      return result;
    } finally { command.dispose?.(); }
  }

  begin(document: EditorDocument, command: LiveCommand): CommandExecution {
    if (this.live) throw new Error('A live command is already active.');
    let result: CommandExecution;
    try { result = normalizedExecution(command, command.execute(document)); }
    catch (error) { command.dispose?.(); throw error; }
    if (result.outcome !== 'changed') { command.dispose?.(); return result; }
    this.live = command;
    this.liveDamage = result.damage;
    this.emit('preview', result.damage);
    return result;
  }

  updateLive(document: EditorDocument, update: (command: LiveCommand) => void): void {
    if (!this.live) return;
    update(this.live);
    const nextDamage = this.live.damage?.() ?? this.liveDamage;
    this.liveDamage = mergeDamage(this.liveDamage, nextDamage);
    this.emit('preview', nextDamage);
  }

  commitLive(document: EditorDocument): CommandExecution {
    if (!this.live) return noOp();
    const command = this.live;
    const result = normalizedExecution(command, command.seal(document));
    this.live = null;
    if (result.outcome !== 'changed') {
      if (result.outcome === 'rejected') command.undo(document);
      command.dispose?.();
      this.emit('cancel', this.liveDamage);
      this.liveDamage = EMPTY_DAMAGE;
      return result;
    }
    this.past.push(command);
    for (const discarded of this.future) this.evict(discarded, 'discarded');
    this.future = [];
    this.revision += 1;
    this.enforceBudget();
    touch(document);
    this.emit('commit', mergeDamage(this.liveDamage, result.damage), command);
    for (const listener of this.commandListeners) listener(command);
    this.liveDamage = EMPTY_DAMAGE;
    return result;
  }

  cancelLive(document: EditorDocument): void {
    if (!this.live) return;
    this.live.undo(document);
    this.live.dispose?.();
    this.live = null;
    this.emit('cancel', this.liveDamage);
    this.liveDamage = EMPTY_DAMAGE;
  }

  undo(document: EditorDocument): void {
    if (this.live) this.commitLive(document);
    const command = this.past.pop();
    if (!command) return;
    command.undo(document);
    this.future.push(command);
    this.revision += 1;
    touch(document);
    this.emit('undo', command.damage?.() ?? damage({ fullDocument: true }), command);
  }

  redo(document: EditorDocument): void {
    if (this.live) this.commitLive(document);
    const command = this.future.pop();
    if (!command) return;
    if (command.redo) command.redo(document);
    else command.execute(document);
    this.past.push(command);
    this.revision += 1;
    touch(document);
    this.emit('redo', command.damage?.() ?? damage({ fullDocument: true }), command);
  }

  jumpTo(document: EditorDocument, cursor: number): void {
    if (!Number.isInteger(cursor)) return;
    const maximum = this.past.length + this.future.length;
    const target = Math.max(0, Math.min(maximum, cursor));
    while (this.past.length > target) this.undo(document);
    while (this.past.length < target) this.redo(document);
  }

  clear(): void {
    for (const command of this.past) this.evict(command, 'cleared');
    this.live?.dispose?.();
    this.past = [];
    for (const discarded of this.future) this.evict(discarded, 'cleared');
    this.future = [];
    this.live = null;
    this.liveDamage = EMPTY_DAMAGE;
    this.emit('reset');
  }

  state(): HistoryState {
    const undo = this.past.at(-1);
    const redo = this.future.at(-1);
    return {
      canUndo: Boolean(undo),
      canRedo: Boolean(redo),
      ...(undo ? { undoLabel: undo.label } : {}),
      ...(redo ? { redoLabel: redo.label } : {}),
      entries: this.past.map((command) => command.label),
      timeline: [...this.past.map((command) => command.label), ...[...this.future].reverse().map((command) => command.label)],
      cursor: this.past.length,
      retainedBytes: [...this.past, ...this.future, ...(this.live ? [this.live] : [])].reduce((sum, command) => sum + retainedCommandBytes(command), 0),
      revision: this.revision,
    };
  }

  private enforceBudget(): void {
    let bytes = this.past.reduce((sum, command) => sum + retainedCommandBytes(command), 0);
    while (this.past.length > 1 && bytes > this.byteBudget) {
      const removed = this.past.shift();
      if (removed) { bytes -= retainedCommandBytes(removed); this.evict(removed, 'budget'); }
    }
  }
}

export class SetActiveLayersCommand implements Command {
  readonly label = 'Select layers';
  private before: LayerId[] = [];

  constructor(private readonly layerIds: readonly LayerId[]) {}

  retainedBytes(): number { return 64 + (this.layerIds.length + this.before.length) * 32; }

  execute(document: EditorDocument): CommandExecution {
    const next = [...new Set(this.layerIds)].filter((id) => Boolean(findLayer(document.root, id)));
    if (next.length === document.activeLayerIds.length && next.every((id, index) => id === document.activeLayerIds[index])) return noOp();
    this.before = document.activeLayerIds;
    document.activeLayerIds = next;
    return changed();
  }

  undo(document: EditorDocument): void {
    document.activeLayerIds = this.before;
  }
}

function cloneMask(mask: NonNullable<LayerBase['mask']>): NonNullable<LayerBase['mask']> {
  return { ...mask, id: createId('mask'), tiles: mask.tiles.clone() };
}

export function cloneLayer(layer: Layer, options: { portableText?: 'prepare' } = {}): Layer {
  if (layer.kind === 'text' && layer.text.schemaVersion === 2 && options.portableText !== 'prepare') throw new Error('Copying portable text requires source identity and layout rebinding.');
  const common: LayerBase = {
    id: createId('layer'),
    name: layer.name,
    visible: layer.visible,
    opacity: layer.opacity,
    fillOpacity: layer.fillOpacity,
    blendMode: layer.blendMode,
    locked: layer.locked,
    pixelsLocked: layer.pixelsLocked,
    positionLocked: layer.positionLocked,
    color: layer.color,
    clipping: layer.clipping,
    transform: [...layer.transform] as unknown as Matrix,
    ...(layer.mask ? { mask: cloneMask(layer.mask) } : {}),
    extensions: structuredClone(layer.extensions),
  };
  if (layer.kind === 'raster') {
    return {
      ...common,
      kind: 'raster',
      raster: layer.raster.clone(),
    };
  }
  if (layer.kind === 'group') {
    return {
      ...common,
      kind: 'group',
      children: layer.children.map(child => cloneLayer(child, options)),
      expanded: layer.expanded,
    };
  }
  if (layer.kind === 'adjustment') return {
    ...common,
    kind: 'adjustment',
    adjustment: structuredClone(layer.adjustment),
  };
  if (layer.kind === 'vector') return {
    ...common,
    kind: 'vector',
    vector: {
      ...structuredClone(layer.vector),
      objects: layer.vector.objects.map((object) => {
        const copy = { ...structuredClone(object), id: createId('vector') };
        if (copy.type === 'path') {
          const nodes = new Map(copy.geometry.nodes.map(node => [node.id, createId('node')]));
          const subpaths = new Map(copy.geometry.subpaths.map(subpath => [subpath.id, createId('subpath')]));
          copy.geometry = {
            ...copy.geometry,
            nodes: copy.geometry.nodes.map(node => ({ ...node, id: nodes.get(node.id)!, subpathId: subpaths.get(node.subpathId)! })),
            subpaths: copy.geometry.subpaths.map(subpath => ({ ...subpath, id: subpaths.get(subpath.id)!, nodeIds: subpath.nodeIds.map(id => nodes.get(id)!) })),
            ...(copy.geometry.widthStops ? { widthStops: copy.geometry.widthStops.map(stop => ({ ...stop, id: createId('width-stop') })) } : {}),
          };
        }
        return copy;
      }),
    },
  };
  if (layer.kind === 'image') return { ...common, kind: 'image', image: structuredClone(layer.image) };
  return { ...common, kind: 'text', text: structuredClone(layer.text) };
}

export class AddLayerCommand implements Command {
  readonly label: string;
  private beforeActive?: LayerId[];

  constructor(
    private readonly layer: Layer,
    private readonly parentId: string = 'root',
    private readonly index = 0,
  ) {
    this.label = `Add ${layer.name}`;
  }

  retainedBytes(): number { return 256 + layerRetainedBytes(this.layer); }

  toActionStep(): ActionStep | null {
    if (this.layer.kind === 'adjustment') return { type: 'add-adjustment', adjustment: structuredClone(this.layer.adjustment) };
    return this.layer.name.endsWith(' copy') ? { type: 'duplicate-active-layer' } : null;
  }

  execute(document: EditorDocument): CommandExecution {
    const parent = this.parentId === 'root' || this.parentId === document.root.id ? document.root : findLayer(document.root, this.parentId);
    if (!parent || parent.kind !== 'group') return rejected('Layer parent no longer exists.');
    if (findLayer(document.root, this.layer.id)) return noOp();
    this.beforeActive ??= [...document.activeLayerIds];
    parent.children.splice(Math.min(this.index, parent.children.length), 0, this.layer);
    document.activeLayerIds = [this.layer.id];
    return changed(damage({ layerIds: [this.layer.id, parent.id], structural: true }));
  }

  undo(document: EditorDocument): void {
    const location = findLayerParent(document.root, this.layer.id);
    if (location) location.parent.children.splice(location.index, 1);
    document.activeLayerIds = this.beforeActive ? [...this.beforeActive] : [];
  }

  damage(): DamageSet { return damage({ layerIds: [this.layer.id, this.parentId], structural: true }); }
}

export class RemoveLayerCommand implements Command {
  readonly label = 'Remove layer';
  private removed?: { layer: Layer; parentId: string; index: number };
  private beforeActive?: LayerId[];

  constructor(private readonly layerId: LayerId) {}

  retainedBytes(): number { return 256 + (this.removed ? layerRetainedBytes(this.removed.layer) : 0); }

  execute(document: EditorDocument): CommandExecution {
    const location = findLayerParent(document.root, this.layerId);
    if (!location) return noOp();
    this.beforeActive ??= [...document.activeLayerIds];
    const [layer] = location.parent.children.splice(location.index, 1);
    if (!layer) return noOp();
    this.removed = { layer, parentId: location.parent === document.root ? 'root' : location.parent.id, index: location.index };
    document.activeLayerIds = [];
    return changed(damage({ layerIds: [this.layerId, location.parent.id], structural: true }));
  }

  undo(document: EditorDocument): void {
    if (!this.removed) return;
    const parent = this.removed.parentId === 'root' ? document.root : findLayer(document.root, this.removed.parentId);
    if (!parent || parent.kind !== 'group') return;
    parent.children.splice(this.removed.index, 0, this.removed.layer);
    document.activeLayerIds = this.beforeActive ? [...this.beforeActive] : [this.removed.layer.id];
  }

  damage(): DamageSet { return damage({ layerIds: [this.layerId, this.removed?.parentId ?? 'root'], structural: true }); }
}

export type LayerPatch = Partial<Pick<LayerBase, 'name' | 'visible' | 'opacity' | 'fillOpacity' | 'blendMode' | 'locked' | 'pixelsLocked' | 'positionLocked' | 'color' | 'clipping'>>;

export class UpdateLayerCommand implements Command {
  private before?: LayerPatch;

  constructor(
    private readonly layerId: LayerId,
    private readonly patch: LayerPatch,
    readonly label: string,
  ) {}

  retainedBytes(): number { return 128 + JSON.stringify(this.patch).length * 2 + JSON.stringify(this.before ?? {}).length * 2; }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer) return rejected('Layer no longer exists.');
    const keys = Object.keys(this.patch) as (keyof LayerPatch)[];
    if (keys.length === 0 || keys.every((key) => layer[key] === this.patch[key])) return noOp();
    if (!this.before) {
      const before: LayerPatch = {};
      for (const key of keys) {
        const value = layer[key];
        Object.assign(before, { [key]: value });
      }
      this.before = before;
    }
    Object.assign(layer, this.patch);
    return changed(damage({ layerIds: [layer.id] }));
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer && this.before) Object.assign(layer, this.before);
  }

  damage(): DamageSet { return damage({ layerIds: [this.layerId] }); }
}

export class ReorderLayerCommand implements Command {
  readonly label = 'Reorder layer';
  private from?: { parentId: string; index: number };

  constructor(
    private readonly layerId: LayerId,
    private readonly targetParentId: string,
    private readonly targetIndex: number,
  ) {}

  retainedBytes(): number { return 160; }

  execute(document: EditorDocument): CommandExecution {
    const location = findLayerParent(document.root, this.layerId);
    const target = this.targetParentId === 'root' ? document.root : findLayer(document.root, this.targetParentId);
    if (!location || !target || target.kind !== 'group') return rejected('Layer or target group no longer exists.');
    const moving = location.parent.children[location.index];
    if (!moving) return noOp();
    if (moving.kind === 'group' && (target.id === moving.id || Boolean(findLayer(moving, target.id)))) {
      return rejected('A group cannot be moved into itself or one of its descendants.');
    }
    const clampedIndex = Math.max(0, Math.min(this.targetIndex, target.children.length));
    if (location.parent === target && (clampedIndex === location.index || clampedIndex === location.index + 1)) return noOp();
    if (!this.from) this.from = { parentId: location.parent === document.root ? 'root' : location.parent.id, index: location.index };
    const [layer] = location.parent.children.splice(location.index, 1);
    if (!layer) return noOp();
    const insertionIndex = location.parent === target && clampedIndex > location.index ? clampedIndex - 1 : clampedIndex;
    target.children.splice(Math.max(0, Math.min(insertionIndex, target.children.length)), 0, layer);
    return changed(damage({ layerIds: [layer.id, location.parent.id, target.id], structural: true }));
  }

  undo(document: EditorDocument): void {
    if (!this.from) return;
    const location = findLayerParent(document.root, this.layerId);
    const parent = this.from.parentId === 'root' ? document.root : findLayer(document.root, this.from.parentId);
    if (!location || !parent || parent.kind !== 'group') return;
    const [layer] = location.parent.children.splice(location.index, 1);
    if (layer) parent.children.splice(this.from.index, 0, layer);
  }

  damage(): DamageSet { return damage({ layerIds: [this.layerId, this.targetParentId], structural: true }); }
}

export class GroupLayersCommand implements Command {
  readonly label = 'Group layers';
  private group?: GroupLayer;
  private originals: { layer: Layer; index: number }[] = [];
  private beforeActive?: LayerId[];

  constructor(private readonly layerIds: readonly LayerId[]) {}

  retainedBytes(): number { return 256 + (this.group ? layerRetainedBytes(this.group) : 0); }

  execute(document: EditorDocument): CommandExecution {
    const locations = this.layerIds
      .map((id) => findLayerParent(document.root, id))
      .filter((value): value is NonNullable<typeof value> => Boolean(value));
    if (locations.length === 0) return noOp();
    if (!locations.every((location) => location.parent === locations[0]?.parent)) return rejected('Only sibling layers can be grouped.');
    const parent = locations[0]?.parent;
    if (!parent) return rejected('Layer parent no longer exists.');
    this.beforeActive ??= [...document.activeLayerIds];
    if (!this.group) {
      this.originals = locations
        .map((location) => ({ layer: location.parent.children[location.index], index: location.index }))
        .filter((item): item is { layer: Layer; index: number } => Boolean(item.layer))
        .sort((a, b) => a.index - b.index);
      this.group = createGroupLayer('Group', this.originals.map((item) => item.layer));
    }
    for (const item of [...this.originals].sort((a, b) => b.index - a.index)) parent.children.splice(item.index, 1);
    parent.children.splice(this.originals[0]?.index ?? 0, 0, this.group);
    document.activeLayerIds = [this.group.id];
    return changed(damage({ layerIds: [parent.id, this.group.id, ...this.originals.map((item) => item.layer.id)], structural: true }));
  }

  undo(document: EditorDocument): void {
    if (!this.group) return;
    const location = findLayerParent(document.root, this.group.id);
    if (!location) return;
    // Put each layer back at its own index, lowest first, so layers that were
    // not adjacent keep their neighbours and a redo removes the right ones.
    location.parent.children.splice(location.index, 1);
    for (const item of this.originals) location.parent.children.splice(item.index, 0, item.layer);
    document.activeLayerIds = this.beforeActive ? [...this.beforeActive] : this.originals.map((item) => item.layer.id);
  }

  damage(): DamageSet { return damage({ layerIds: [this.group?.id ?? 'root', ...this.layerIds], structural: true }); }
}

export class UngroupLayerCommand implements Command {
  readonly label = 'Ungroup layers';
  private location?: { parentId: string; index: number };
  private group?: GroupLayer;
  private beforeActive?: LayerId[];

  constructor(private readonly groupId: LayerId) {}

  retainedBytes(): number { return 256 + (this.group ? layerRetainedBytes(this.group) : 0); }

  execute(document: EditorDocument): CommandExecution {
    const found = findLayer(document.root, this.groupId);
    const location = findLayerParent(document.root, this.groupId);
    if (!found || found.kind !== 'group' || !location) return rejected('Group no longer exists.');
    this.beforeActive ??= [...document.activeLayerIds];
    this.group ??= found;
    this.location ??= { parentId: location.parent === document.root ? 'root' : location.parent.id, index: location.index };
    location.parent.children.splice(location.index, 1, ...found.children);
    document.activeLayerIds = found.children.map((layer) => layer.id);
    return changed(damage({ layerIds: [found.id, location.parent.id, ...found.children.map((layer) => layer.id)], structural: true }));
  }

  undo(document: EditorDocument): void {
    if (!this.group || !this.location) return;
    const parent = this.location.parentId === 'root' ? document.root : findLayer(document.root, this.location.parentId);
    if (!parent || parent.kind !== 'group') return;
    const childIds = new Set(this.group.children.map((layer) => layer.id));
    const firstIndex = parent.children.findIndex((layer) => childIds.has(layer.id));
    if (firstIndex < 0) return;
    parent.children = parent.children.filter((layer) => !childIds.has(layer.id));
    parent.children.splice(firstIndex, 0, this.group);
    document.activeLayerIds = this.beforeActive ? [...this.beforeActive] : [this.group.id];
  }

  damage(): DamageSet { return damage({ layerIds: [this.groupId, ...(this.group?.children.map((layer) => layer.id) ?? [])], structural: true }); }
}

export class SetGroupExpandedCommand implements Command {
  readonly label: string;
  private before?: boolean;

  constructor(private readonly groupId: LayerId, private readonly expanded: boolean) {
    this.label = expanded ? 'Expand group' : 'Collapse group';
  }

  retainedBytes(): number { return 64; }

  execute(document: EditorDocument): CommandExecution {
    const group = findLayer(document.root, this.groupId);
    if (!group || group.kind !== 'group') return rejected('Group no longer exists.');
    if (group.expanded === this.expanded) return noOp();
    this.before ??= group.expanded;
    group.expanded = this.expanded;
    return changed();
  }

  undo(document: EditorDocument): void {
    const group = findLayer(document.root, this.groupId);
    if (group?.kind === 'group' && this.before !== undefined) group.expanded = this.before;
  }
}

export class UpdateAdjustmentCommand implements Command {
  readonly label = 'Change adjustment';
  private before?: import('./model').AdjustmentLayer['adjustment'];

  constructor(
    private readonly layerId: LayerId,
    private readonly patch: Partial<import('./model').AdjustmentLayer['adjustment']>,
  ) {}

  retainedBytes(): number { return 128 + JSON.stringify(this.patch).length * 2 + JSON.stringify(this.before ?? {}).length * 2; }

  toActionStep(): ActionStep { return { type: 'update-active-adjustment', patch: structuredClone(this.patch) }; }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'adjustment') return rejected('Adjustment layer no longer exists.');
    if (Object.entries(this.patch).every(([key, value]) => layer.adjustment[key as keyof typeof this.patch] === value)) return noOp();
    this.before ??= structuredClone(layer.adjustment);
    Object.assign(layer.adjustment, this.patch);
    return changed(damage({ layerIds: [this.layerId] }));
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'adjustment' && this.before) layer.adjustment = structuredClone(this.before);
  }
}

interface DocumentGeometrySnapshot {
  width: number;
  height: number;
  resolution: EditorDocument['resolution'];
  selection: SelectionState;
  guides?: Guide[];
  transforms: Map<LayerId, Matrix>;
}

function cloneSelection(selection: SelectionState): SelectionState {
  return {
    regions: selection.regions.map((region) => ({ ...region })),
    inverted: selection.inverted,
  };
}

function captureDocumentGeometry(document: EditorDocument): DocumentGeometrySnapshot {
  return {
    width: document.width,
    height: document.height,
    resolution: { ...document.resolution },
    selection: cloneSelection(document.selection),
    ...(document.guides ? { guides: document.guides.map((guide) => ({ ...guide })) } : {}),
    // Only TOP-LEVEL layers (root's direct children) are captured — including
    // groups. A document-wide operation left-multiplies each captured transform
    // (see DocumentGeometryCommand), so applying it to a top-level group's own
    // transform correctly moves the group's whole subtree. Recursing into nested
    // leaves instead would apply the operation on the wrong side of an ancestor
    // group's transform and misposition content inside a moved group.
    transforms: new Map(document.root.children
      .filter((layer) => layer.kind !== 'adjustment')
      .map((layer) => [layer.id, layer.transform])),
  };
}

function applyDocumentGeometry(document: EditorDocument, snapshot: DocumentGeometrySnapshot): void {
  document.width = snapshot.width;
  document.height = snapshot.height;
  document.resolution = { ...snapshot.resolution };
  document.selection = cloneSelection(snapshot.selection);
  if (snapshot.guides) document.guides = snapshot.guides.map((guide) => ({ ...guide }));
  else delete document.guides;
  for (const [layerId, transform] of snapshot.transforms) {
    const layer = findLayer(document.root, layerId);
    if (layer) layer.transform = transform;
  }
}

function documentGeometryEqual(left: DocumentGeometrySnapshot, right: DocumentGeometrySnapshot): boolean {
  if (left.width !== right.width || left.height !== right.height || JSON.stringify(left.resolution) !== JSON.stringify(right.resolution)) return false;
  if (JSON.stringify(left.selection) !== JSON.stringify(right.selection) || JSON.stringify(left.guides) !== JSON.stringify(right.guides)) return false;
  if (left.transforms.size !== right.transforms.size) return false;
  for (const [id, matrix] of left.transforms) {
    const other = right.transforms.get(id);
    if (!other || matrix.some((value, index) => value !== other[index])) return false;
  }
  return true;
}

function transformedSelection(selection: SelectionState, operation: Matrix, width: number, height: number): SelectionState {
  const canvas = { x: 0, y: 0, width, height };
  return {
    regions: selection.regions
      .map((region) => transformRectBounds(operation, region))
      .map((region) => intersectRect(region, canvas))
      .filter((region): region is Rect => Boolean(region)),
    inverted: selection.inverted,
  };
}

function transformedGuides(
  guides: readonly Guide[] | undefined,
  operation: Matrix,
  sourceWidth: number,
  sourceHeight: number,
  width: number,
  height: number,
): Guide[] | undefined {
  if (!guides) return undefined;
  return guides.map((guide) => {
    const start = guide.axis === 'vertical'
      ? transformPoint(operation, { x: guide.position, y: 0 })
      : transformPoint(operation, { x: 0, y: guide.position });
    const end = guide.axis === 'vertical'
      ? transformPoint(operation, { x: guide.position, y: sourceHeight })
      : transformPoint(operation, { x: sourceWidth, y: guide.position });
    const vertical = Math.abs(start.x - end.x) < Math.abs(start.y - end.y);
    return {
      ...guide,
      axis: vertical ? 'vertical' : 'horizontal',
      position: vertical
        ? Math.max(0, Math.min(width, start.x))
        : Math.max(0, Math.min(height, start.y)),
    };
  });
}

function transformDocumentSnapshot(
  before: DocumentGeometrySnapshot,
  width: number,
  height: number,
  operation: Matrix,
  resolution: EditorDocument['resolution'] = before.resolution,
): DocumentGeometrySnapshot {
  validateDimensions(width, height);
  const guides = transformedGuides(before.guides, operation, before.width, before.height, width, height);
  return {
    width,
    height,
    resolution: { ...resolution },
    selection: transformedSelection(before.selection, operation, width, height),
    ...(guides ? { guides } : {}),
    transforms: new Map([...before.transforms].map(([id, transform]) => [id, multiplyMatrix(operation, transform)])),
  };
}

abstract class DocumentGeometryCommand implements Command {
  abstract readonly label: string;
  private before?: DocumentGeometrySnapshot;
  private after?: DocumentGeometrySnapshot;

  retainedBytes(): number {
    return 512 + (this.before?.transforms.size ?? 0) * 64;
  }

  execute(document: EditorDocument): CommandExecution {
    this.before ??= captureDocumentGeometry(document);
    try {
      this.after ??= this.createAfter(this.before);
    } catch (error) {
      return rejected(error instanceof Error ? error.message : 'Document geometry is invalid.');
    }
    if (documentGeometryEqual(this.before, this.after)) return noOp();
    applyDocumentGeometry(document, this.after);
    return changed(damage({ fullDocument: true, structural: true }));
  }

  undo(document: EditorDocument): void {
    if (this.before) applyDocumentGeometry(document, this.before);
  }

  redo(document: EditorDocument): void {
    if (this.after) applyDocumentGeometry(document, this.after);
  }

  damage(): DamageSet { return damage({ fullDocument: true, structural: true }); }

  protected abstract createAfter(before: DocumentGeometrySnapshot): DocumentGeometrySnapshot;
}

export class ResizeImageCommand extends DocumentGeometryCommand {
  readonly label = 'Resize image';

  constructor(
    private readonly width: number,
    private readonly height: number,
    private readonly resolution?: number,
  ) { super(); }

  protected createAfter(before: DocumentGeometrySnapshot): DocumentGeometrySnapshot {
    const nextResolution = this.resolution === undefined
      ? before.resolution
      : { x: this.resolution, y: this.resolution, unit: 'ppi' as const };
    return transformDocumentSnapshot(
      before,
      this.width,
      this.height,
      [this.width / before.width, 0, 0, this.height / before.height, 0, 0],
      nextResolution,
    );
  }
}

export type CanvasAnchor = 'top-left' | 'top' | 'top-right' | 'left' | 'center' | 'right' | 'bottom-left' | 'bottom' | 'bottom-right';

export class ResizeCanvasCommand extends DocumentGeometryCommand {
  readonly label = 'Resize canvas';

  constructor(
    private readonly width: number,
    private readonly height: number,
    private readonly anchor: CanvasAnchor = 'center',
  ) { super(); }

  protected createAfter(before: DocumentGeometrySnapshot): DocumentGeometrySnapshot {
    const horizontal = this.anchor.endsWith('left') || this.anchor === 'left' ? 0
      : this.anchor.endsWith('right') || this.anchor === 'right' ? this.width - before.width
        : (this.width - before.width) / 2;
    const vertical = this.anchor.startsWith('top') || this.anchor === 'top' ? 0
      : this.anchor.startsWith('bottom') || this.anchor === 'bottom' ? this.height - before.height
        : (this.height - before.height) / 2;
    return transformDocumentSnapshot(before, this.width, this.height, [1, 0, 0, 1, horizontal, vertical]);
  }
}

export class CropDocumentCommand extends DocumentGeometryCommand {
  readonly label = 'Crop document';

  constructor(private readonly requestedRect: Rect) { super(); }

  protected createAfter(before: DocumentGeometrySnapshot): DocumentGeometrySnapshot {
    const rect = intersectRect(normalizeRect(this.requestedRect), { x: 0, y: 0, width: before.width, height: before.height });
    if (!rect) throw new Error('Crop area does not intersect the canvas.');
    return transformDocumentSnapshot(before, rect.width, rect.height, [1, 0, 0, 1, -rect.x, -rect.y]);
  }
}

export type DocumentRotation = 'clockwise' | 'counterclockwise' | 'half-turn';

export class RotateDocumentCommand extends DocumentGeometryCommand {
  readonly label: string;

  constructor(private readonly direction: DocumentRotation) {
    super();
    this.label = direction === 'half-turn' ? 'Rotate document 180°' : `Rotate document 90° ${direction}`;
  }

  protected createAfter(before: DocumentGeometrySnapshot): DocumentGeometrySnapshot {
    if (this.direction === 'clockwise') {
      return transformDocumentSnapshot(before, before.height, before.width, [0, 1, -1, 0, before.height, 0]);
    }
    if (this.direction === 'counterclockwise') {
      return transformDocumentSnapshot(before, before.height, before.width, [0, -1, 1, 0, 0, before.width]);
    }
    return transformDocumentSnapshot(before, before.width, before.height, [-1, 0, 0, -1, before.width, before.height]);
  }
}

export class FlipDocumentCommand extends DocumentGeometryCommand {
  readonly label: string;

  constructor(private readonly axis: 'horizontal' | 'vertical') {
    super();
    this.label = `Flip document ${axis}`;
  }

  protected createAfter(before: DocumentGeometrySnapshot): DocumentGeometrySnapshot {
    const operation: Matrix = this.axis === 'horizontal'
      ? [-1, 0, 0, 1, before.width, 0]
      : [1, 0, 0, -1, 0, before.height];
    return transformDocumentSnapshot(before, before.width, before.height, operation);
  }
}

export class AddGuideCommand implements Command {
  readonly label = 'Add guide';
  private readonly guide: Guide;
  /** Whether the document had a guides list before; undo restores its absence byte for byte. */
  private hadGuides?: boolean;

  constructor(axis: Guide['axis'], position: number) {
    this.guide = { id: createId('guide'), axis, position };
  }

  retainedBytes(): number { return 128 + (this.guide.color?.length ?? 0) * 2; }

  execute(document: EditorDocument): CommandExecution {
    if (!Number.isFinite(this.guide.position)) return rejected('Guide position must be finite.');
    const limit = this.guide.axis === 'vertical' ? document.width : document.height;
    this.guide.position = Math.max(0, Math.min(limit, this.guide.position));
    this.hadGuides ??= document.guides !== undefined;
    document.guides = [...(document.guides ?? []).filter((guide) => guide.id !== this.guide.id), this.guide];
    return changed();
  }

  undo(document: EditorDocument): void {
    const rest = (document.guides ?? []).filter((guide) => guide.id !== this.guide.id);
    if (rest.length === 0 && this.hadGuides === false) delete document.guides;
    else document.guides = rest;
  }
}

export class ClearGuidesCommand implements Command {
  readonly label = 'Clear guides';
  private before?: Guide[];

  retainedBytes(): number { return 128 + JSON.stringify(this.before ?? []).length * 2; }

  execute(document: EditorDocument): CommandExecution {
    if (!document.guides?.length) return noOp();
    this.before ??= document.guides?.map((guide) => ({ ...guide })) ?? [];
    document.guides = [];
    return changed();
  }

  undo(document: EditorDocument): void {
    document.guides = this.before?.map((guide) => ({ ...guide })) ?? [];
  }
}

export class SelectionCommand implements Command {
  private before?: SelectionState;
  private after?: SelectionState;

  constructor(
    readonly label: string,
    private readonly update: (selection: SelectionState, document: EditorDocument) => SelectionState,
  ) {}

  retainedBytes(): number { return 128 + JSON.stringify(this.before ?? {}).length * 2 + JSON.stringify(this.after ?? {}).length * 2; }

  execute(document: EditorDocument): CommandExecution {
    this.before ??= document.selection;
    this.after ??= this.update(document.selection, document);
    if (JSON.stringify(document.selection) === JSON.stringify(this.after)) return noOp();
    document.selection = this.after;
    return changed();
  }

  undo(document: EditorDocument): void {
    if (this.before) document.selection = this.before;
  }

  static rectangle(rect: Rect, mode: SelectionCombineMode): SelectionCommand {
    return new SelectionCommand(`${mode === 'replace' ? 'Select' : mode} rectangle`, (selection) => combineRectangle(selection, rect, mode));
  }

  static regions(regions: readonly Rect[], mode: SelectionCombineMode, label = 'Select path'): SelectionCommand {
    return new SelectionCommand(label, (selection) => combineRegions(selection, regions, mode));
  }

  static clear(): SelectionCommand {
    return new SelectionCommand('Clear selection', () => ({ regions: [], inverted: false }));
  }

  static all(): SelectionCommand {
    return new SelectionCommand('Select all', (_selection, document) => ({ regions: [{ x: 0, y: 0, width: document.width, height: document.height }], inverted: false }));
  }

  static invert(): SelectionCommand {
    return new SelectionCommand('Invert selection', (selection) => ({ ...selection, inverted: !selection.inverted }));
  }
}

export class TransformLayerCommand implements MatrixTransformCommand {
  readonly label = 'Transform layer';
  private before?: Matrix;
  private after?: Matrix;

  constructor(private readonly layerId: LayerId) {}

  retainedBytes(): number { return 128 + (this.before ? 48 : 0) + (this.after ? 48 : 0); }

  toActionStep(): ActionStep | null { return this.after ? { type: 'transform-active-layer', transform: [...this.after] as unknown as Matrix } : null; }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer) return rejected('Layer no longer exists.');
    if (layer.locked || layer.positionLocked) return rejected('Layer position is locked.');
    this.before ??= layer.transform;
    if (this.after) layer.transform = this.after;
    return changed(damage({ layerIds: [this.layerId] }));
  }

  update(document: EditorDocument, transform: Matrix): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer && !layer.positionLocked && !layer.locked) {
      layer.transform = transform;
      this.after = transform;
    }
  }

  seal(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || !this.before) return rejected('Layer no longer exists.');
    this.after = layer.transform;
    return this.before.every((value, index) => value === this.after?.[index]) ? noOp() : changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer && this.before) layer.transform = this.before;
  }

  redo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer && this.after) layer.transform = this.after;
  }

  damage(): DamageSet { return damage({ layerIds: [this.layerId] }); }
}

export interface BrushSettings {
  size: number;
  hardness: number;
  opacity: number;
  flow: number;
  spacing: number;
  color: readonly [number, number, number, number];
  mode: 'paint' | 'erase';
  angle?: number;
  roundness?: number;
  scatter?: number;
  count?: number;
  texture?: number;
  pressureSize?: boolean;
  pressureOpacity?: boolean;
  /** 0-1 deterministic per-dab reduction in diameter. */
  sizeJitter?: number;
  /** 0-1 deterministic per-dab reduction in flow. */
  opacityJitter?: number;
  tip?: SampledBrushTip;
}

export interface SampledBrushTip {
  width: number;
  height: number;
  /** Row-major coverage bytes. Black source pixels define opaque brush marks. */
  alpha: readonly number[];
}

export interface StrokePoint extends Point {
  pressure: number;
}

type SampleBlend = 'paint' | 'restore' | 'clone' | 'heal';

function circularSelectionRegions(point: Point, diameter: number): Rect[] {
  const radius = Math.max(0.5, diameter / 2);
  const top = Math.floor(point.y - radius);
  const bottom = Math.ceil(point.y + radius);
  const regions: Rect[] = [];
  for (let y = top; y < bottom; y += 1) {
    const dy = y + 0.5 - point.y;
    const halfWidth = Math.sqrt(Math.max(0, radius * radius - dy * dy));
    const left = Math.ceil(point.x - halfWidth - 0.5);
    const right = Math.ceil(point.x + halfWidth - 0.5);
    if (right > left) regions.push({ x: left, y, width: right - left, height: 1 });
  }
  return regions;
}

/**
 * Full layer-to-document matrix for painting.
 *
 * A raster layer nested in a transformed group has `layer.transform` !=
 * `layerDocumentTransform`. Stroke commands map the pointer into layer space
 * with one of them and test the selection with the other, so mixing the two
 * put the selection test in a different space from the stroke itself; using
 * `layer.transform` alone ignored the group transform in both directions and
 * landed the stroke in the wrong place entirely.
 */
function paintMatrix(document: EditorDocument, layer: Layer): Matrix {
  return layerDocumentTransform(document.root, layer.id) ?? layer.transform;
}

export class QuickMaskStrokeCommand implements LiveCommand {
  readonly label: string;
  private before?: SelectionState;
  private after?: SelectionState;
  private lastPoint?: StrokePoint;

  constructor(
    private readonly settings: BrushSettings,
    private readonly firstPoint: StrokePoint,
    private readonly mode: 'add' | 'subtract',
  ) {
    this.label = mode === 'add' ? 'Reveal quick mask' : 'Hide quick mask';
  }

  retainedBytes(): number {
    return 256 + JSON.stringify(this.before ?? {}).length * 2 + JSON.stringify(this.after ?? {}).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    if (this.after) {
      document.selection = { regions: this.after.regions.map((region) => ({ ...region })), inverted: this.after.inverted };
      return changed();
    }
    this.before = { regions: document.selection.regions.map((region) => ({ ...region })), inverted: document.selection.inverted };
    this.append(document, this.firstPoint);
    return changed();
  }

  append(document: EditorDocument, point: StrokePoint): void {
    const previous = this.lastPoint;
    const distance = previous ? Math.hypot(point.x - previous.x, point.y - previous.y) : 0;
    const step = Math.max(1, this.settings.size * Math.max(0.01, this.settings.spacing));
    const count = previous ? Math.max(1, Math.ceil(distance / step)) : 1;
    for (let index = 1; index <= count; index += 1) {
      const amount = previous ? index / count : 1;
      const center = previous ? { x: previous.x + (point.x - previous.x) * amount, y: previous.y + (point.y - previous.y) * amount } : point;
      if (document.selection.regions.length === 0 && !document.selection.inverted) {
        if (this.mode === 'add') continue;
        document.selection = { regions: [], inverted: true };
      }
      document.selection = combineRegions(document.selection, circularSelectionRegions(center, this.settings.size), this.mode);
    }
    this.lastPoint = point;
  }

  seal(document: EditorDocument): CommandExecution {
    this.after = { regions: document.selection.regions.map((region) => ({ ...region })), inverted: document.selection.inverted };
    return JSON.stringify(this.before) === JSON.stringify(this.after) ? noOp() : changed();
  }

  undo(document: EditorDocument): void {
    if (this.before) document.selection = { regions: this.before.regions.map((region) => ({ ...region })), inverted: this.before.inverted };
  }

  redo(document: EditorDocument): void {
    if (this.after) document.selection = { regions: this.after.regions.map((region) => ({ ...region })), inverted: this.after.inverted };
  }
}

interface TileDelta {
  x: number;
  y: number;
  pixels: Uint8ClampedArray | null;
}

function sampleRasterPixel(store: RasterTileStore, x: number, y: number): readonly [number, number, number, number] | null {
  if (x < 0 || y < 0 || x >= store.width || y >= store.height) return null;
  const tileX = Math.floor(x / store.tileSize);
  const tileY = Math.floor(y / store.tileSize);
  const tile = store.getTile(tileX, tileY);
  if (!tile) return [0, 0, 0, 0];
  const dimensions = store.tileDimensions(tileX, tileY);
  const offset = ((y - tileY * store.tileSize) * dimensions.width + (x - tileX * store.tileSize)) * 4;
  return [tile[offset] ?? 0, tile[offset + 1] ?? 0, tile[offset + 2] ?? 0, tile[offset + 3] ?? 0];
}

/**
 * Coverage of one brush dab at a point already rotated into tip space.
 *
 * Shared by the stroke and by the options-bar preview so the swatch cannot
 * drift from what the brush actually paints.
 */
export function brushTipCoverage(
  tip: SampledBrushTip | undefined,
  rotatedX: number,
  rotatedY: number,
  radiusX: number,
  radiusY: number,
  hardness: number,
): number {
  if (tip) {
    const normalizedX = rotatedX / radiusX;
    const normalizedY = rotatedY / radiusY;
    if (Math.abs(normalizedX) > 1 || Math.abs(normalizedY) > 1) return 0;
    const tipX = Math.max(0, Math.min(tip.width - 1, Math.floor((normalizedX * 0.5 + 0.5) * tip.width)));
    const tipY = Math.max(0, Math.min(tip.height - 1, Math.floor((normalizedY * 0.5 + 0.5) * tip.height)));
    return (tip.alpha[tipY * tip.width + tipX] ?? 0) / 255;
  }
  const distance = Math.hypot(rotatedX / radiusX, rotatedY / radiusY);
  if (distance > 1) return 0;
  if (distance <= hardness) return 1;
  return hardness >= 0.999 ? 1 : Math.max(0, Math.min(1, (1 - distance) / (1 - hardness)));
}

export class PaintStrokeCommand implements LiveCommand {
  readonly label: string;
  private readonly before = new Map<string, TileDelta>();
  private readonly after = new Map<string, TileDelta>();
  private readonly accumulation = new Map<string, Float32Array>();
  private lastPoint?: StrokePoint;
  private changedBounds?: Rect;
  private readonly source: RasterTileStore | null;
  private readonly sourceOffset: Point;
  private readonly sampleBlend: SampleBlend;
  private readonly sourceMapper: ((point: Point) => Point) | undefined;
  private stampIndex = 0;

  constructor(
    private readonly layerId: LayerId,
    private readonly settings: BrushSettings,
    private readonly firstPoint: StrokePoint,
    source: RasterTileStore | null = null,
    label?: string,
    sourceOffset: Point = { x: 0, y: 0 },
    sampleBlend: SampleBlend = source ? 'restore' : 'paint',
    sourceMapper?: (point: Point) => Point,
  ) {
    this.source = source;
    this.sourceOffset = sourceOffset;
    this.sampleBlend = sampleBlend;
    this.sourceMapper = sourceMapper;
    this.label = label ?? (settings.mode === 'erase' ? 'Erase stroke' : 'Brush stroke');
  }

  retainedBytes(): number {
    let total = 128;
    for (const delta of this.before.values()) total += delta.pixels?.byteLength ?? 0;
    for (const delta of this.after.values()) total += delta.pixels?.byteLength ?? 0;
    for (const values of this.accumulation.values()) total += values.byteLength;
    if (this.source) total += this.source.byteLength();
    return total;
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'raster') return rejected('Paint target is not a raster layer.');
    if (layer.locked || layer.pixelsLocked) return rejected('Layer pixels are locked.');
    if (!invertMatrix(paintMatrix(document, layer))) return rejected('Layer transform is not invertible.');
    if (this.after.size > 0) {
      this.restore(document, this.after);
      return changed(this.damage());
    }
    this.append(document, this.firstPoint);
    return changed(this.damage());
  }

  append(document: EditorDocument, point: StrokePoint): void {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'raster' || layer.locked || layer.pixelsLocked) return;
    const inverse = invertMatrix(paintMatrix(document, layer));
    if (!inverse) return;
    const localPoint = transformPoint(inverse, point);
    const previous = this.lastPoint;
    if (!previous) {
      this.paintStamp(document, layer, localPoint, point.pressure);
      this.lastPoint = point;
      return;
    }
    const previousLocal = transformPoint(inverse, previous);
    const distance = Math.hypot(localPoint.x - previousLocal.x, localPoint.y - previousLocal.y);
    const step = Math.max(1, this.settings.size * Math.max(0.01, this.settings.spacing));
    const count = Math.max(1, Math.ceil(distance / step));
    for (let index = 1; index <= count; index += 1) {
      const ratio = index / count;
      this.paintStamp(
        document,
        layer,
        {
          x: previousLocal.x + (localPoint.x - previousLocal.x) * ratio,
          y: previousLocal.y + (localPoint.y - previousLocal.y) * ratio,
        },
        previous.pressure + (point.pressure - previous.pressure) * ratio,
      );
    }
    this.lastPoint = point;
  }

  private paintStamp(document: EditorDocument, layer: RasterLayer, point: Point, pressure: number): void {
    const count = Math.max(1, Math.min(8, Math.round(this.settings.count ?? 1)));
    const scatter = Math.max(0, Math.min(2, this.settings.scatter ?? 0));
    for (let copy = 0; copy < count; copy += 1) {
      const index = this.stampIndex * 11 + copy + 1;
      const randomX = ((index * 137) % 997) / 997 * 2 - 1;
      const randomY = ((index * 283) % 991) / 991 * 2 - 1;
      const sizeJitter = Math.max(0, Math.min(1, this.settings.sizeJitter ?? 0));
      const opacityJitter = Math.max(0, Math.min(1, this.settings.opacityJitter ?? 0));
      const sizeScale = 1 - sizeJitter * (((index * 419) % 983) / 983);
      const opacityScale = 1 - opacityJitter * (((index * 613) % 977) / 977);
      this.paintDab(document, layer, {
        x: point.x + randomX * this.settings.size * scatter / 2,
        y: point.y + randomY * this.settings.size * scatter / 2,
      }, pressure, sizeScale, opacityScale);
    }
    this.stampIndex += 1;
  }

  seal(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'raster') return rejected('Paint target no longer exists.');
    if (this.before.size === 0) return noOp();
    for (const delta of this.before.values()) {
      const pixels = layer.raster.getTile(delta.x, delta.y);
      this.after.set(RasterTileStore.key(delta.x, delta.y), {
        x: delta.x,
        y: delta.y,
        pixels: pixels ? new Uint8ClampedArray(pixels) : null,
      });
    }
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    this.restore(document, this.before);
  }

  redo(document: EditorDocument): void {
    this.restore(document, this.after);
  }

  private restore(document: EditorDocument, deltas: ReadonlyMap<string, TileDelta>): void {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'raster') return;
    for (const delta of deltas.values()) layer.raster.setTile(delta.x, delta.y, delta.pixels);
  }

  private capture(store: RasterTileStore, tileX: number, tileY: number): void {
    const key = RasterTileStore.key(tileX, tileY);
    if (this.before.has(key)) return;
    const pixels = store.getTile(tileX, tileY);
    this.before.set(key, { x: tileX, y: tileY, pixels: pixels ? new Uint8ClampedArray(pixels) : null });
  }

  private paintDab(document: EditorDocument, layer: RasterLayer, point: Point, pressure: number, sizeScale = 1, opacityScale = 1): void {
    const touched = new Set<string>();
    const effectivePressure = Math.max(0, Math.min(1, Number.isFinite(pressure) ? pressure : 1));
    const pressureScale = this.settings.pressureSize ? 0.1 + effectivePressure * 0.9 : 1;
    const radiusX = Math.max(0.5, this.settings.size * pressureScale * Math.max(0.05, sizeScale) / 2);
    const radiusY = Math.max(0.5, radiusX * Math.max(0.05, Math.min(1, this.settings.roundness ?? 1)));
    const radius = Math.max(radiusX, radiusY);
    const hardness = Math.max(0, Math.min(1, this.settings.hardness));
    const angle = (this.settings.angle ?? 0) * Math.PI / 180;
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    const minX = Math.max(0, Math.floor(point.x - radius));
    const minY = Math.max(0, Math.floor(point.y - radius));
    const maxX = Math.min(layer.raster.width - 1, Math.ceil(point.x + radius));
    const maxY = Math.min(layer.raster.height - 1, Math.ceil(point.y + radius));
    const opacityPressure = this.settings.pressureOpacity === false ? 1 : effectivePressure;
    const documentMatrix = paintMatrix(document, layer);
    const healShift = this.sampleBlend === 'heal' && this.source
      ? this.healColorShift(point, radius)
      : null;
    for (let y = minY; y <= maxY; y += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        const dx = x + 0.5 - point.x;
        const dy = y + 0.5 - point.y;
        const rotatedX = cosine * dx + sine * dy;
        const rotatedY = -sine * dx + cosine * dy;
        const tipCoverage = brushTipCoverage(this.settings.tip, rotatedX, rotatedY, radiusX, radiusY, hardness);
        if (tipCoverage <= 0) continue;
        const documentPoint = transformPoint(documentMatrix, { x: x + 0.5, y: y + 0.5 });
        if (!pointIsSelected(document.selection, documentPoint)) continue;
        const textureNoise = (((x * 73 + y * 151) % 97) / 96);
        const texture = 1 - Math.max(0, Math.min(1, this.settings.texture ?? 0)) * textureNoise;
        const contribution = tipCoverage * texture * this.settings.flow * opacityPressure * opacityScale * (this.settings.color[3] / 255);
        if (contribution <= 0) continue;
        this.changedBounds = this.changedBounds ? {
          x: Math.min(this.changedBounds.x, documentPoint.x - 0.5),
          y: Math.min(this.changedBounds.y, documentPoint.y - 0.5),
          width: Math.max(this.changedBounds.x + this.changedBounds.width, documentPoint.x + 0.5) - Math.min(this.changedBounds.x, documentPoint.x - 0.5),
          height: Math.max(this.changedBounds.y + this.changedBounds.height, documentPoint.y + 0.5) - Math.min(this.changedBounds.y, documentPoint.y - 0.5),
        } : { x: documentPoint.x - 0.5, y: documentPoint.y - 0.5, width: 1, height: 1 };
        const tileX = Math.floor(x / layer.raster.tileSize);
        const tileY = Math.floor(y / layer.raster.tileSize);
        touched.add(RasterTileStore.key(tileX, tileY));
        this.capture(layer.raster, tileX, tileY);
        const tile = layer.raster.getTile(tileX, tileY, true);
        if (!tile) continue;
        const dimensions = layer.raster.tileDimensions(tileX, tileY);
        const localX = x - tileX * layer.raster.tileSize;
        const localY = y - tileY * layer.raster.tileSize;
        const offset = (localY * dimensions.width + localX) * 4;
        const pixelIndex = localY * dimensions.width + localX;
        const key = RasterTileStore.key(tileX, tileY);
        let accumulated = this.accumulation.get(key);
        if (!accumulated) {
          accumulated = new Float32Array(dimensions.width * dimensions.height);
          this.accumulation.set(key, accumulated);
        }
        const previousCoverage = accumulated[pixelIndex] ?? 0;
        const strokeCoverage = 1 - (1 - previousCoverage) * (1 - contribution);
        accumulated[pixelIndex] = strokeCoverage;
        const alpha = strokeCoverage * this.settings.opacity;
        const original = this.before.get(key)?.pixels;
        if (this.source) {
          const sourcePoint = this.sourceMapper
            ? this.sourceMapper({ x: x + 0.5, y: y + 0.5 })
            : { x: x + this.sourceOffset.x, y: y + this.sourceOffset.y };
          const sourceX = Math.floor(sourcePoint.x);
          const sourceY = Math.floor(sourcePoint.y);
          if (!Number.isFinite(sourceX) || !Number.isFinite(sourceY) || sourceX < 0 || sourceY < 0 || sourceX >= this.source.width || sourceY >= this.source.height) continue;
          const sourceTileX = Math.floor(sourceX / this.source.tileSize);
          const sourceTileY = Math.floor(sourceY / this.source.tileSize);
          const sourceTile = this.source.getTile(sourceTileX, sourceTileY);
          const sourceDimensions = this.source.tileDimensions(sourceTileX, sourceTileY);
          const sourceLocalX = sourceX - sourceTileX * this.source.tileSize;
          const sourceLocalY = sourceY - sourceTileY * this.source.tileSize;
          const sourcePixelOffset = (sourceLocalY * sourceDimensions.width + sourceLocalX) * 4;
          if (this.sampleBlend === 'restore') {
            for (let channel = 0; channel < 4; channel += 1) {
              const source = sourceTile?.[sourcePixelOffset + channel] ?? 0;
              const destination = original?.[offset + channel] ?? 0;
              tile[offset + channel] = Math.round(source * alpha + destination * (1 - alpha));
            }
            continue;
          }
          const sourceAlpha = ((sourceTile?.[sourcePixelOffset + 3] ?? 0) / 255) * alpha;
          const destinationAlpha = (original?.[offset + 3] ?? 0) / 255;
          const outputAlpha = sourceAlpha + destinationAlpha * (1 - sourceAlpha);
          if (outputAlpha <= 0) continue;
          for (let channel = 0; channel < 3; channel += 1) {
            const sampled = sourceTile?.[sourcePixelOffset + channel] ?? 0;
            const source = Math.max(0, Math.min(255, sampled + (healShift?.[channel] ?? 0)));
            const destination = original?.[offset + channel] ?? 0;
            tile[offset + channel] = Math.round((source * sourceAlpha + destination * destinationAlpha * (1 - sourceAlpha)) / outputAlpha);
          }
          tile[offset + 3] = Math.round(outputAlpha * 255);
          continue;
        }
        const destinationAlpha = (original?.[offset + 3] ?? 0) / 255;
        if (this.settings.mode === 'erase') {
          tile[offset + 3] = Math.round(destinationAlpha * (1 - alpha) * 255);
          continue;
        }
        const outputAlpha = alpha + destinationAlpha * (1 - alpha);
        if (outputAlpha <= 0) continue;
        for (let channel = 0; channel < 3; channel += 1) {
          const source = this.settings.color[channel] ?? 0;
          const destination = original?.[offset + channel] ?? 0;
          tile[offset + channel] = Math.round((source * alpha + destination * destinationAlpha * (1 - alpha)) / outputAlpha);
        }
        tile[offset + 3] = Math.round(outputAlpha * 255);
      }
    }
    for (const key of touched) {
      const [x, y] = key.split(':').map(Number);
      layer.raster.touchTile(x ?? 0, y ?? 0);
    }
  }

  private healColorShift(point: Point, radius: number): readonly [number, number, number] {
    if (!this.source) return [0, 0, 0];
    const sourceTotals = [0, 0, 0];
    const destinationTotals = [0, 0, 0];
    let count = 0;
    const step = Math.max(1, Math.round(radius / 6));
    for (let y = Math.floor(point.y - radius); y <= point.y + radius; y += step) {
      for (let x = Math.floor(point.x - radius); x <= point.x + radius; x += step) {
        if (Math.hypot(x + 0.5 - point.x, y + 0.5 - point.y) > radius) continue;
        const destination = sampleRasterPixel(this.source, x, y);
        const sampled = sampleRasterPixel(this.source, Math.floor(x + this.sourceOffset.x), Math.floor(y + this.sourceOffset.y));
        if (!destination || !sampled || destination[3] === 0 || sampled[3] === 0) continue;
        for (let channel = 0; channel < 3; channel += 1) {
          destinationTotals[channel] = (destinationTotals[channel] ?? 0) + (destination[channel] ?? 0);
          sourceTotals[channel] = (sourceTotals[channel] ?? 0) + (sampled[channel] ?? 0);
        }
        count += 1;
      }
    }
    if (!count) return [0, 0, 0];
    return [
      ((destinationTotals[0] ?? 0) - (sourceTotals[0] ?? 0)) / count,
      ((destinationTotals[1] ?? 0) - (sourceTotals[1] ?? 0)) / count,
      ((destinationTotals[2] ?? 0) - (sourceTotals[2] ?? 0)) / count,
    ];
  }

  damage(): DamageSet {
    return damage({
      layerIds: [this.layerId],
      tiles: [...this.before.values()].map((tile) => ({ layerId: this.layerId, x: tile.x, y: tile.y, target: 'pixels' as const })),
      bounds: this.changedBounds ? [this.changedBounds] : [],
    });
  }
}

export class HistoryBrushStrokeCommand extends PaintStrokeCommand {
  constructor(layerId: LayerId, settings: BrushSettings, firstPoint: StrokePoint, source: RasterTileStore) {
    super(layerId, { ...settings, mode: 'paint' }, firstPoint, source, 'History brush stroke');
  }
}

export class CloneStampCommand extends PaintStrokeCommand {
  constructor(layerId: LayerId, settings: BrushSettings, firstPoint: StrokePoint, source: RasterTileStore, offset: Point) {
    super(layerId, { ...settings, mode: 'paint' }, firstPoint, source, 'Clone stamp stroke', offset, 'clone');
  }
}

export class HealStrokeCommand extends PaintStrokeCommand {
  constructor(layerId: LayerId, settings: BrushSettings, firstPoint: StrokePoint, source: RasterTileStore, offset: Point) {
    super(layerId, { ...settings, mode: 'paint' }, firstPoint, source, 'Healing brush stroke', offset, 'heal');
  }
}

export class HealSelectionCommand implements Command {
  readonly label = 'Heal selected patch';
  private before?: RasterTileStore;
  private after?: RasterTileStore;

  constructor(private readonly layerId: LayerId) {}

  retainedBytes(): number {
    return 256 + (this.before?.byteLength() ?? 0) + (this.after?.byteLength() ?? 0);
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'raster') return rejected('Patch target is not a raster layer.');
    if (layer.locked || layer.pixelsLocked) return rejected('Layer pixels are locked.');
    if (this.after) {
      layer.raster = this.after;
      return changed(this.damage());
    }
    if (document.selection.inverted || document.selection.regions.length === 0) return rejected('Make a finite selection before healing a patch.');
    const effective = paintMatrix(document, layer);
    const inverse = invertMatrix(effective);
    if (!inverse) return rejected('Layer transform is not invertible.');
    const selected = selectionBounds(document.selection, { x: 0, y: 0, width: document.width, height: document.height });
    if (!selected) return noOp();
    const local = transformRectBounds(inverse, selected);
    const startX = Math.max(0, Math.floor(local.x));
    const startY = Math.max(0, Math.floor(local.y));
    const endX = Math.min(layer.raster.width, Math.ceil(local.x + local.width));
    const endY = Math.min(layer.raster.height, Math.ceil(local.y + local.height));
    if ((endX - startX) * (endY - startY) > 1_000_000) return rejected('Patch selection exceeds the 1 megapixel safety limit.');
    const dx = endX + (endX - startX) + 8 <= layer.raster.width ? endX - startX + 8 : -(endX - startX + 8);
    const sourceTotals = [0, 0, 0];
    const destinationTotals = [0, 0, 0];
    let sourceCount = 0;
    for (let y = startY; y < endY; y += 1) for (let x = startX; x < endX; x += 1) {
      if (!pointIsSelected(document.selection, transformPoint(effective, { x: x + 0.5, y: y + 0.5 }))) continue;
      const source = sampleRasterPixel(layer.raster, x + dx, y);
      if (!source || source[3] === 0) continue;
      for (let channel = 0; channel < 3; channel += 1) {
        sourceTotals[channel] = (sourceTotals[channel] ?? 0) + (source[channel] ?? 0);
      }
      sourceCount += 1;
    }
    let destinationCount = 0;
    for (let y = Math.max(0, startY - 3); y < Math.min(layer.raster.height, endY + 3); y += 1) {
      for (let x = Math.max(0, startX - 3); x < Math.min(layer.raster.width, endX + 3); x += 1) {
        if (x >= startX && x < endX && y >= startY && y < endY) continue;
        const destination = sampleRasterPixel(layer.raster, x, y);
        if (!destination || destination[3] === 0) continue;
        for (let channel = 0; channel < 3; channel += 1) destinationTotals[channel] = (destinationTotals[channel] ?? 0) + (destination[channel] ?? 0);
        destinationCount += 1;
      }
    }
    if (!sourceCount) return rejected('No usable source pixels are available beside the selection.');
    if (!destinationCount) {
      for (let channel = 0; channel < 3; channel += 1) destinationTotals[channel] = sourceTotals[channel] ?? 0;
      destinationCount = sourceCount;
    }
    const shift = destinationTotals.map((total, channel) => total / destinationCount - (sourceTotals[channel] ?? 0) / sourceCount);
    const next = layer.raster.clone();
    let changedPixels = 0;
    const feather = Math.max(1, Math.min(3, Math.min(endX - startX, endY - startY) / 2));
    for (let y = startY; y < endY; y += 1) for (let x = startX; x < endX; x += 1) {
      if (!pointIsSelected(document.selection, transformPoint(effective, { x: x + 0.5, y: y + 0.5 }))) continue;
      const source = sampleRasterPixel(layer.raster, x + dx, y);
      const destination = sampleRasterPixel(layer.raster, x, y);
      if (!source || !destination || source[3] === 0) continue;
      const edgeDistance = Math.min(x - startX + 1, endX - x, y - startY + 1, endY - y);
      const alpha = Math.min(1, edgeDistance / feather);
      const tileX = Math.floor(x / next.tileSize);
      const tileY = Math.floor(y / next.tileSize);
      const tile = next.getTile(tileX, tileY, true);
      if (!tile) continue;
      const dimensions = next.tileDimensions(tileX, tileY);
      const offset = ((y - tileY * next.tileSize) * dimensions.width + (x - tileX * next.tileSize)) * 4;
      for (let channel = 0; channel < 3; channel += 1) {
        const corrected = Math.max(0, Math.min(255, (source[channel] ?? 0) + (shift[channel] ?? 0)));
        tile[offset + channel] = Math.round(corrected * alpha + (destination[channel] ?? 0) * (1 - alpha));
      }
      tile[offset + 3] = Math.round((source[3] ?? 0) * alpha + (destination[3] ?? 0) * (1 - alpha));
      next.touchTile(tileX, tileY);
      changedPixels += 1;
    }
    if (!changedPixels) return noOp();
    this.before = layer.raster;
    this.after = next;
    layer.raster = next;
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'raster' && this.before) layer.raster = this.before;
  }

  redo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'raster' && this.after) layer.raster = this.after;
  }

  damage(): DamageSet {
    return damage({ layerIds: [this.layerId], structural: true });
  }
}

export class PaintMaskStrokeCommand implements LiveCommand {
  readonly label = 'Refine layer mask';
  private readonly before = new Map<string, TileDelta>();
  private readonly after = new Map<string, TileDelta>();
  private readonly accumulation = new Map<string, Float32Array>();
  private lastPoint?: StrokePoint;

  constructor(
    private readonly layerId: LayerId,
    private readonly maskId: string,
    private readonly settings: BrushSettings,
    private readonly firstPoint: StrokePoint,
  ) {}

  retainedBytes(): number {
    let total = 128;
    for (const delta of this.before.values()) total += delta.pixels?.byteLength ?? 0;
    for (const delta of this.after.values()) total += delta.pixels?.byteLength ?? 0;
    for (const values of this.accumulation.values()) total += values.byteLength;
    return total;
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'raster' || layer.mask?.id !== this.maskId) return rejected('Mask paint target no longer exists.');
    if (layer.locked) return rejected('Layer is locked.');
    if (!invertMatrix(paintMatrix(document, layer))) return rejected('Layer transform is not invertible.');
    if (this.after.size > 0) this.restore(document, this.after);
    else this.append(document, this.firstPoint);
    return changed(this.damage());
  }

  append(document: EditorDocument, point: StrokePoint): void {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'raster' || layer.mask?.id !== this.maskId || layer.locked) return;
    const inverse = invertMatrix(paintMatrix(document, layer));
    if (!inverse) return;
    const localPoint = transformPoint(inverse, point);
    const previous = this.lastPoint;
    if (!previous) {
      this.paintDab(document, layer, localPoint, point.pressure);
      this.lastPoint = point;
      return;
    }
    const previousLocal = transformPoint(inverse, previous);
    const distance = Math.hypot(localPoint.x - previousLocal.x, localPoint.y - previousLocal.y);
    const step = Math.max(1, this.settings.size * Math.max(0.01, this.settings.spacing));
    const count = Math.max(1, Math.ceil(distance / step));
    for (let index = 1; index <= count; index += 1) {
      const ratio = index / count;
      this.paintDab(document, layer, {
        x: previousLocal.x + (localPoint.x - previousLocal.x) * ratio,
        y: previousLocal.y + (localPoint.y - previousLocal.y) * ratio,
      }, previous.pressure + (point.pressure - previous.pressure) * ratio);
    }
    this.lastPoint = point;
  }

  seal(document: EditorDocument): CommandExecution {
    const store = this.maskStore(document);
    if (!store) return rejected('Mask paint target no longer exists.');
    if (this.before.size === 0) return noOp();
    for (const delta of this.before.values()) {
      const pixels = store.getTile(delta.x, delta.y);
      this.after.set(RasterTileStore.key(delta.x, delta.y), {
        x: delta.x,
        y: delta.y,
        pixels: pixels ? new Uint8ClampedArray(pixels) : null,
      });
    }
    return changed(this.damage());
  }

  undo(document: EditorDocument): void { this.restore(document, this.before); }
  redo(document: EditorDocument): void { this.restore(document, this.after); }

  private maskStore(document: EditorDocument): RasterTileStore | undefined {
    const layer = findLayer(document.root, this.layerId);
    return layer?.kind === 'raster' && layer.mask?.id === this.maskId ? layer.mask.tiles : undefined;
  }

  private restore(document: EditorDocument, deltas: ReadonlyMap<string, TileDelta>): void {
    const store = this.maskStore(document);
    if (!store) return;
    for (const delta of deltas.values()) store.setTile(delta.x, delta.y, delta.pixels);
  }

  private capture(store: RasterTileStore, tileX: number, tileY: number): void {
    const key = RasterTileStore.key(tileX, tileY);
    if (this.before.has(key)) return;
    const pixels = store.getTile(tileX, tileY);
    this.before.set(key, { x: tileX, y: tileY, pixels: pixels ? new Uint8ClampedArray(pixels) : null });
  }

  private paintDab(document: EditorDocument, layer: RasterLayer, point: Point, pressure: number): void {
    const mask = layer.mask;
    if (!mask || mask.id !== this.maskId) return;
    const store = mask.tiles;
    const radius = Math.max(0.5, this.settings.size / 2);
    const inner = radius * Math.max(0, Math.min(1, this.settings.hardness));
    const minX = Math.max(0, Math.floor(point.x - radius));
    const minY = Math.max(0, Math.floor(point.y - radius));
    const maxX = Math.min(store.width - 1, Math.ceil(point.x + radius));
    const maxY = Math.min(store.height - 1, Math.ceil(point.y + radius));
    const effectivePressure = Math.max(0, Math.min(1, Number.isFinite(pressure) ? pressure : 1));
    const paintedAlpha = this.settings.mode === 'erase'
      ? 255
      : Math.round(0.2126 * this.settings.color[0] + 0.7152 * this.settings.color[1] + 0.0722 * this.settings.color[2]);
    const touched = new Set<string>();
    const documentMatrix = paintMatrix(document, layer);
    for (let y = minY; y <= maxY; y += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        const distance = Math.hypot(x + 0.5 - point.x, y + 0.5 - point.y);
        if (distance > radius) continue;
        if (!pointIsSelected(document.selection, transformPoint(documentMatrix, { x: x + 0.5, y: y + 0.5 }))) continue;
        const edge = radius === inner ? 1 : Math.max(0, Math.min(1, (radius - distance) / (radius - inner)));
        const contribution = (distance <= inner ? 1 : edge) * this.settings.flow * effectivePressure;
        if (contribution <= 0) continue;
        const tileX = Math.floor(x / store.tileSize);
        const tileY = Math.floor(y / store.tileSize);
        touched.add(RasterTileStore.key(tileX, tileY));
        const existing = store.getTile(tileX, tileY);
        this.capture(store, tileX, tileY);
        const tile = existing ?? store.getTile(tileX, tileY, true);
        if (!tile) continue;
        if (!existing) {
          for (let offset = 0; offset < tile.length; offset += 4) {
            tile[offset] = 255;
            tile[offset + 1] = 255;
            tile[offset + 2] = 255;
            tile[offset + 3] = mask.defaultAlpha;
          }
        }
        const dimensions = store.tileDimensions(tileX, tileY);
        const localX = x - tileX * store.tileSize;
        const localY = y - tileY * store.tileSize;
        const offset = (localY * dimensions.width + localX) * 4;
        const pixelIndex = localY * dimensions.width + localX;
        const key = RasterTileStore.key(tileX, tileY);
        let accumulated = this.accumulation.get(key);
        if (!accumulated) {
          accumulated = new Float32Array(dimensions.width * dimensions.height);
          this.accumulation.set(key, accumulated);
        }
        const previousCoverage = accumulated[pixelIndex] ?? 0;
        const strokeCoverage = 1 - (1 - previousCoverage) * (1 - contribution);
        accumulated[pixelIndex] = strokeCoverage;
        const original = this.before.get(key)?.pixels?.[offset + 3] ?? mask.defaultAlpha;
        tile[offset + 3] = Math.round(original + (paintedAlpha - original) * strokeCoverage * this.settings.opacity);
      }
    }
    for (const key of touched) {
      const [x, y] = key.split(':').map(Number);
      store.touchTile(x ?? 0, y ?? 0);
    }
  }

  damage(): DamageSet {
    return damage({
      layerIds: [this.layerId],
      tiles: [...this.before.values()].map((tile) => ({ layerId: this.layerId, x: tile.x, y: tile.y, target: 'mask' as const })),
    });
  }
}

export type FillDefinition =
  | { type: 'solid'; color: readonly [number, number, number, number] }
  | {
    type: 'linear-gradient';
    start: Point;
    end: Point;
    startColor: readonly [number, number, number, number];
    endColor: readonly [number, number, number, number];
  };

function fillColor(definition: FillDefinition, point: Point): readonly [number, number, number, number] {
  if (definition.type === 'solid') return definition.color;
  const dx = definition.end.x - definition.start.x;
  const dy = definition.end.y - definition.start.y;
  const lengthSquared = dx * dx + dy * dy;
  const ratio = lengthSquared <= 1e-6
    ? 0
    : Math.max(0, Math.min(1, ((point.x - definition.start.x) * dx + (point.y - definition.start.y) * dy) / lengthSquared));
  return [
    Math.round(definition.startColor[0] + (definition.endColor[0] - definition.startColor[0]) * ratio),
    Math.round(definition.startColor[1] + (definition.endColor[1] - definition.startColor[1]) * ratio),
    Math.round(definition.startColor[2] + (definition.endColor[2] - definition.startColor[2]) * ratio),
    Math.round(definition.startColor[3] + (definition.endColor[3] - definition.startColor[3]) * ratio),
  ];
}

export class FillRasterCommand implements Command {
  readonly label: string;
  private readonly before = new Map<string, TileDelta>();
  private readonly after = new Map<string, TileDelta>();

  private readonly region: Rect | undefined;

  /**
   * `options.region` fills an explicit document-space rectangle instead of the
   * user's pixel selection, so callers that must not depend on hidden UI state
   * (agents, ID-addressed intents) can fill without reading the selection.
   */
  constructor(private readonly layerId: LayerId, private readonly definition: FillDefinition, options: { region?: Rect } = {}) {
    this.label = definition.type === 'solid' ? 'Fill pixels' : 'Linear gradient';
    this.region = options.region ? { ...options.region } : undefined;
  }

  retainedBytes(): number {
    let total = 128;
    for (const delta of this.before.values()) total += delta.pixels?.byteLength ?? 0;
    for (const delta of this.after.values()) total += delta.pixels?.byteLength ?? 0;
    return total;
  }

  // A region fill is not the recorded "fill the selection" step: replaying it
  // would read whatever the user has selected at replay time.
  toActionStep(): ActionStep | null { return this.region ? null : { type: 'fill-active-raster', definition: structuredClone(this.definition) }; }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'raster') return rejected('Fill target is not a raster layer.');
    if (layer.locked || layer.pixelsLocked) return rejected('Layer pixels are locked.');
    if (this.after.size) {
      this.restore(layer.raster, this.after);
      return changed(this.damage());
    }
    const inverse = invertMatrix(paintMatrix(document, layer));
    if (!inverse) return rejected('Layer transform is not invertible.');
    const selection: SelectionState = this.region ? { regions: [this.region], inverted: false } : document.selection;
    let bounds: Rect = { x: 0, y: 0, width: layer.raster.width, height: layer.raster.height };
    if (!selection.inverted && selection.regions.length > 0) {
      const localRegions = selection.regions.map((region) => transformRectBounds(inverse, region));
      const left = Math.min(...localRegions.map((region) => region.x));
      const top = Math.min(...localRegions.map((region) => region.y));
      const right = Math.max(...localRegions.map((region) => region.x + region.width));
      const bottom = Math.max(...localRegions.map((region) => region.y + region.height));
      bounds = intersectRect({ x: left, y: top, width: right - left, height: bottom - top }, bounds) ?? { x: 0, y: 0, width: 0, height: 0 };
    }
    const startX = Math.max(0, Math.floor(bounds.x));
    const startY = Math.max(0, Math.floor(bounds.y));
    const endX = Math.min(layer.raster.width, Math.ceil(bounds.x + bounds.width));
    const endY = Math.min(layer.raster.height, Math.ceil(bounds.y + bounds.height));
    const documentMatrix = paintMatrix(document, layer);
    for (let y = startY; y < endY; y += 1) {
      for (let x = startX; x < endX; x += 1) {
        const documentPoint = transformPoint(documentMatrix, { x: x + 0.5, y: y + 0.5 });
        if (!pointIsSelected(selection, documentPoint)) continue;
        const tileX = Math.floor(x / layer.raster.tileSize);
        const tileY = Math.floor(y / layer.raster.tileSize);
        const key = RasterTileStore.key(tileX, tileY);
        if (!this.before.has(key)) {
          const current = layer.raster.getTile(tileX, tileY);
          this.before.set(key, { x: tileX, y: tileY, pixels: current ? new Uint8ClampedArray(current) : null });
        }
        const tile = layer.raster.getTile(tileX, tileY, true);
        if (!tile) continue;
        const dimensions = layer.raster.tileDimensions(tileX, tileY);
        const localX = x - tileX * layer.raster.tileSize;
        const localY = y - tileY * layer.raster.tileSize;
        const offset = (localY * dimensions.width + localX) * 4;
        const source = fillColor(this.definition, documentPoint);
        const sourceAlpha = (source[3] ?? 255) / 255;
        const destinationAlpha = (tile[offset + 3] ?? 0) / 255;
        const outputAlpha = sourceAlpha + destinationAlpha * (1 - sourceAlpha);
        for (let channel = 0; channel < 3; channel += 1) {
          const sourceValue = source[channel] ?? 0;
          const destination = tile[offset + channel] ?? 0;
          tile[offset + channel] = outputAlpha <= 0 ? 0 : Math.round((sourceValue * sourceAlpha + destination * destinationAlpha * (1 - sourceAlpha)) / outputAlpha);
        }
        tile[offset + 3] = Math.round(outputAlpha * 255);
      }
    }
    for (const delta of this.before.values()) {
      const pixels = layer.raster.getTile(delta.x, delta.y);
      this.after.set(RasterTileStore.key(delta.x, delta.y), { x: delta.x, y: delta.y, pixels: pixels ? new Uint8ClampedArray(pixels) : null });
      layer.raster.touchTile(delta.x, delta.y);
    }
    return this.before.size > 0 ? changed(this.damage()) : noOp();
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'raster') this.restore(layer.raster, this.before);
  }

  redo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'raster') this.restore(layer.raster, this.after);
  }

  private restore(store: RasterTileStore, deltas: ReadonlyMap<string, TileDelta>): void {
    for (const delta of deltas.values()) store.setTile(delta.x, delta.y, delta.pixels);
  }

  damage(): DamageSet {
    return damage({
      layerIds: [this.layerId],
      tiles: [...this.before.values()].map((tile) => ({ layerId: this.layerId, x: tile.x, y: tile.y, target: 'pixels' as const })),
    });
  }
}

export class ContentAwareFillCommand implements Command {
  readonly label = 'Content-aware fill';
  private readonly before = new Map<string, TileDelta>();
  private readonly after = new Map<string, TileDelta>();

  constructor(private readonly layerId: LayerId) {}

  toActionStep(): ActionStep { return { type: 'content-aware-fill' }; }

  retainedBytes(): number {
    return 128 + [...this.before.values(), ...this.after.values()].reduce((total, delta) => total + (delta.pixels?.byteLength ?? 0), 0);
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'raster') return rejected('Content-aware fill requires a raster layer.');
    if (layer.locked || layer.pixelsLocked) return rejected('Layer pixels are locked.');
    if (document.selection.inverted || document.selection.regions.length === 0) return rejected('Make a finite selection before using content-aware fill.');
    if (this.after.size > 0) {
      this.restore(layer.raster, this.after);
      return changed(this.damage());
    }
    const inverse = invertMatrix(paintMatrix(document, layer));
    if (!inverse) return rejected('Layer transform is not invertible.');
    const localRegions = document.selection.regions
      .map((region) => intersectRect(transformRectBounds(inverse, region), { x: 0, y: 0, width: layer.raster.width, height: layer.raster.height }))
      .filter((region): region is Rect => Boolean(region));
    const selectedArea = localRegions.reduce((sum, region) => sum + Math.ceil(region.width) * Math.ceil(region.height), 0);
    if (selectedArea > 16_000_000) return rejected('Content-aware fill selection exceeds the 16 megapixel safety limit.');

    const sample = (x: number, y: number): readonly [number, number, number, number] => {
      const safeX = Math.max(0, Math.min(layer.raster.width - 1, Math.round(x)));
      const safeY = Math.max(0, Math.min(layer.raster.height - 1, Math.round(y)));
      const tileX = Math.floor(safeX / layer.raster.tileSize);
      const tileY = Math.floor(safeY / layer.raster.tileSize);
      const dimensions = layer.raster.tileDimensions(tileX, tileY);
      const offset = ((safeY - tileY * layer.raster.tileSize) * dimensions.width + safeX - tileX * layer.raster.tileSize) * 4;
      const tile = layer.raster.getTile(tileX, tileY);
      return [tile?.[offset] ?? 0, tile?.[offset + 1] ?? 0, tile?.[offset + 2] ?? 0, tile?.[offset + 3] ?? 0];
    };

    const documentMatrix = paintMatrix(document, layer);
    for (const region of localRegions) {
      const left = Math.max(0, Math.floor(region.x));
      const top = Math.max(0, Math.floor(region.y));
      const right = Math.min(layer.raster.width, Math.ceil(region.x + region.width));
      const bottom = Math.min(layer.raster.height, Math.ceil(region.y + region.height));
      for (let y = top; y < bottom; y += 1) {
        for (let x = left; x < right; x += 1) {
          if (!pointIsSelected(document.selection, transformPoint(documentMatrix, { x: x + 0.5, y: y + 0.5 }))) continue;
          const edges = [
            { edge: 'left' as const, distance: x - left, available: left > 0 },
            { edge: 'right' as const, distance: right - 1 - x, available: right < layer.raster.width },
            { edge: 'top' as const, distance: y - top, available: top > 0 },
            { edge: 'bottom' as const, distance: bottom - 1 - y, available: bottom < layer.raster.height },
          ].sort((a, b) => Number(b.available) - Number(a.available) || a.distance - b.distance);
          const preferred = edges[0];
          if (!preferred?.available) continue;
          const depth = Math.min(32, preferred.distance + 1);
          const jitter = ((x * 73856093 ^ y * 19349663) >>> 0) % 9 - 4;
          let sourceX = x;
          let sourceY = y;
          if (preferred.edge === 'left') { sourceX = left - depth; sourceY += jitter; }
          else if (preferred.edge === 'right') { sourceX = right - 1 + depth; sourceY += jitter; }
          else if (preferred.edge === 'top') { sourceY = top - depth; sourceX += jitter; }
          else { sourceY = bottom - 1 + depth; sourceX += jitter; }
          const color = sample(sourceX, sourceY);
          const tileX = Math.floor(x / layer.raster.tileSize);
          const tileY = Math.floor(y / layer.raster.tileSize);
          const key = RasterTileStore.key(tileX, tileY);
          if (!this.before.has(key)) {
            const current = layer.raster.getTile(tileX, tileY);
            this.before.set(key, { x: tileX, y: tileY, pixels: current ? new Uint8ClampedArray(current) : null });
          }
          const tile = layer.raster.getTile(tileX, tileY, true);
          if (!tile) continue;
          const dimensions = layer.raster.tileDimensions(tileX, tileY);
          const offset = ((y - tileY * layer.raster.tileSize) * dimensions.width + x - tileX * layer.raster.tileSize) * 4;
          for (let channel = 0; channel < 4; channel += 1) tile[offset + channel] = color[channel] ?? 0;
        }
      }
    }
    for (const delta of this.before.values()) {
      layer.raster.touchTile(delta.x, delta.y);
      const pixels = layer.raster.getTile(delta.x, delta.y);
      this.after.set(RasterTileStore.key(delta.x, delta.y), { x: delta.x, y: delta.y, pixels: pixels ? new Uint8ClampedArray(pixels) : null });
    }
    return this.before.size > 0 ? changed(this.damage()) : noOp();
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'raster') this.restore(layer.raster, this.before);
  }

  redo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'raster') this.restore(layer.raster, this.after);
  }

  private restore(store: RasterTileStore, deltas: ReadonlyMap<string, TileDelta>): void {
    for (const delta of deltas.values()) store.setTile(delta.x, delta.y, delta.pixels);
  }

  damage(): DamageSet {
    return damage({
      layerIds: [this.layerId],
      tiles: [...this.before.values()].map((tile) => ({ layerId: this.layerId, x: tile.x, y: tile.y, target: 'pixels' as const })),
    });
  }
}

export class ClearRasterSelectionCommand implements Command {
  readonly label = 'Clear selected pixels';
  private readonly before = new Map<string, TileDelta>();
  private readonly after = new Map<string, TileDelta>();

  constructor(private readonly layerId: LayerId) {}

  toActionStep(): ActionStep { return { type: 'clear-selected-pixels' }; }

  retainedBytes(): number {
    let total = 128;
    for (const delta of this.before.values()) total += delta.pixels?.byteLength ?? 0;
    for (const delta of this.after.values()) total += delta.pixels?.byteLength ?? 0;
    return total;
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'raster') return rejected('The active layer is not a raster layer.');
    if (layer.locked || layer.pixelsLocked) return rejected('The active layer pixels are locked.');
    if (this.after.size > 0) {
      this.restore(layer.raster, this.after);
      return changed(this.damage());
    }
    const documentMatrix = paintMatrix(document, layer);
    for (const entry of layer.raster.entries()) {
      const dimensions = layer.raster.tileDimensions(entry.x, entry.y);
      let captured = false;
      for (let y = 0; y < dimensions.height; y += 1) for (let x = 0; x < dimensions.width; x += 1) {
        const offset = (y * dimensions.width + x) * 4;
        if ((entry.pixels[offset + 3] ?? 0) === 0) continue;
        const local = { x: entry.x * layer.raster.tileSize + x + 0.5, y: entry.y * layer.raster.tileSize + y + 0.5 };
        if (!pointIsSelected(document.selection, transformPoint(documentMatrix, local))) continue;
        if (!captured) {
          this.before.set(RasterTileStore.key(entry.x, entry.y), { x: entry.x, y: entry.y, pixels: new Uint8ClampedArray(entry.pixels) });
          captured = true;
        }
        entry.pixels[offset] = 0;
        entry.pixels[offset + 1] = 0;
        entry.pixels[offset + 2] = 0;
        entry.pixels[offset + 3] = 0;
      }
      if (captured) this.after.set(RasterTileStore.key(entry.x, entry.y), { x: entry.x, y: entry.y, pixels: new Uint8ClampedArray(entry.pixels) });
      if (captured) layer.raster.touchTile(entry.x, entry.y);
    }
    return this.before.size > 0 ? changed(this.damage()) : noOp();
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'raster') this.restore(layer.raster, this.before);
  }

  redo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'raster') this.restore(layer.raster, this.after);
  }

  damage(): DamageSet {
    return damage({
      layerIds: [this.layerId],
      tiles: [...this.before.values()].map((tile) => ({ layerId: this.layerId, x: tile.x, y: tile.y, target: 'pixels' as const })),
    });
  }

  private restore(store: RasterTileStore, deltas: ReadonlyMap<string, TileDelta>): void {
    for (const delta of deltas.values()) store.setTile(delta.x, delta.y, delta.pixels);
  }
}

export class CreateMaskCommand implements Command {
  readonly label = 'Add layer mask';
  private previous?: LayerBase['mask'];
  private created?: NonNullable<LayerBase['mask']>;

  constructor(private readonly layerId: LayerId) {}

  retainedBytes(): number { return 256 + (this.previous?.tiles.byteLength() ?? 0) + (this.created?.tiles.byteLength() ?? 0); }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'raster') return rejected('Masks can currently be created only on raster layers.');
    if (this.created) {
      layer.mask = this.created;
      return changed(this.damage());
    }
    if (layer.mask) return noOp();
    this.previous = layer.mask;
    const hasExplicitSelection = document.selection.regions.length > 0 || document.selection.inverted;
    const tiles = new RasterTileStore(layer.raster.width, layer.raster.height);
    const documentMatrix = paintMatrix(document, layer);
    if (hasExplicitSelection) {
      const columns = Math.ceil(tiles.width / tiles.tileSize);
      const rows = Math.ceil(tiles.height / tiles.tileSize);
      for (let tileY = 0; tileY < rows; tileY += 1) {
        for (let tileX = 0; tileX < columns; tileX += 1) {
          const dimensions = tiles.tileDimensions(tileX, tileY);
          const pixels = new Uint8ClampedArray(dimensions.width * dimensions.height * 4);
          let used = false;
          for (let y = 0; y < dimensions.height; y += 1) {
            for (let x = 0; x < dimensions.width; x += 1) {
              const local = { x: tileX * tiles.tileSize + x, y: tileY * tiles.tileSize + y };
              const selected = pointIsSelected(document.selection, transformPoint(documentMatrix, local));
              if (!selected) continue;
              const offset = (y * dimensions.width + x) * 4;
              pixels[offset] = 255;
              pixels[offset + 1] = 255;
              pixels[offset + 2] = 255;
              pixels[offset + 3] = 255;
              used = true;
            }
          }
          if (used) tiles.setTile(tileX, tileY, pixels);
        }
      }
    }
    this.created = {
      id: createId('mask'),
      enabled: true,
      linked: true,
      defaultAlpha: hasExplicitSelection ? 0 : 255,
      tiles,
    };
    layer.mask = this.created;
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (!layer) return;
    if (this.previous) layer.mask = this.previous;
    else delete layer.mask;
  }

  damage(): DamageSet { return damage({ layerIds: [this.layerId] }); }
}

export class UpdateMaskCommand implements Command {
  readonly label = 'Update layer mask';
  private before?: Pick<NonNullable<LayerBase['mask']>, 'enabled' | 'linked'>;

  constructor(
    private readonly layerId: LayerId,
    private readonly patch: Partial<Pick<NonNullable<LayerBase['mask']>, 'enabled' | 'linked'>>,
  ) {}

  retainedBytes(): number { return 128; }

  execute(document: EditorDocument): CommandExecution {
    const mask = findLayer(document.root, this.layerId)?.mask;
    if (!mask) return rejected('Layer mask no longer exists.');
    if (this.patch.linked === false) return rejected('Independent mask transforms are not supported yet.');
    if (Object.entries(this.patch).every(([key, value]) => mask[key as keyof typeof this.patch] === value)) return noOp();
    this.before ??= { enabled: mask.enabled, linked: mask.linked };
    Object.assign(mask, this.patch);
    return changed(damage({ layerIds: [this.layerId] }));
  }

  undo(document: EditorDocument): void {
    const mask = findLayer(document.root, this.layerId)?.mask;
    if (mask && this.before) Object.assign(mask, this.before);
  }
}

export class InvertMaskCommand implements Command {
  readonly label = 'Invert layer mask';

  constructor(private readonly layerId: LayerId) {}

  retainedBytes(): number { return 128; }

  execute(document: EditorDocument): CommandExecution {
    if (!findLayer(document.root, this.layerId)?.mask) return rejected('Layer mask no longer exists.');
    this.invert(document);
    return changed(this.damage());
  }
  undo(document: EditorDocument): void { this.invert(document); }

  damage(): DamageSet { return damage({ layerIds: [this.layerId] }); }

  private invert(document: EditorDocument): void {
    const mask = findLayer(document.root, this.layerId)?.mask;
    if (!mask) return;
    mask.defaultAlpha = mask.defaultAlpha === 255 ? 0 : 255;
    for (const { pixels } of mask.tiles.entries()) {
      for (let offset = 3; offset < pixels.length; offset += 4) pixels[offset] = 255 - (pixels[offset] ?? 0);
    }
    for (const entry of mask.tiles.entries()) mask.tiles.touchTile(entry.x, entry.y);
  }
}

export class RemoveMaskCommand implements Command {
  readonly label = 'Remove layer mask';
  private previous?: NonNullable<LayerBase['mask']>;

  constructor(private readonly layerId: LayerId) {}

  retainedBytes(): number { return (this.previous?.tiles.byteLength() ?? 0) + 256; }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer?.mask) return noOp();
    this.previous ??= layer.mask;
    delete layer.mask;
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer && this.previous) layer.mask = this.previous;
  }

  damage(): DamageSet { return damage({ layerIds: [this.layerId] }); }
}

function matricesEqual(left: Matrix, right: Matrix): boolean {
  return left.every((value, index) => value === right[index]);
}

/**
 * Sets a layer transform in one discrete step. This is the non-live
 * counterpart of `TransformLayerCommand`, whose value can only be set through
 * the begin/updateLive/commitLive gesture protocol.
 */
export class SetLayerTransformCommand implements Command {
  private before?: Matrix;
  private readonly after: Matrix;

  constructor(private readonly layerId: LayerId, transform: Matrix, readonly label = 'Transform layer') {
    this.after = [...transform] as unknown as Matrix;
  }

  retainedBytes(): number { return 160; }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer) return rejected('Layer no longer exists.');
    if (layer.locked || layer.positionLocked) return rejected('Layer position is locked.');
    if (!this.after.every(Number.isFinite) || !invertMatrix(this.after)) return rejected('Layer transform must be finite and invertible.');
    if (matricesEqual(layer.transform, this.after)) return noOp();
    this.before ??= layer.transform;
    layer.transform = this.after;
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer && this.before) layer.transform = this.before;
  }

  redo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer && this.before) layer.transform = this.after;
  }

  damage(): DamageSet { return damage({ layerIds: [this.layerId] }); }
}

export interface VectorPaintPatch {
  /** Replacement fill; `null` removes it. Omitted leaves fills unchanged. */
  fill?: SolidPaint | null;
  /** Replacement stroke; `null` removes it. Omitted leaves strokes unchanged. */
  stroke?: VectorStroke | null;
}

/**
 * Replaces fill and/or stroke on vector objects from plain data. A
 * serializable alternative to the closure-based `UpdateVectorObjectCommand`
 * path for paint changes.
 */
export class SetVectorPaintCommand implements Command {
  readonly label: string;
  private before?: Map<string, { fill: SolidPaint | null; stroke: VectorStroke | null }>;
  private readonly patch: VectorPaintPatch;
  private readonly objectIds: readonly string[] | '*';

  constructor(private readonly layerId: LayerId, objectIds: readonly string[] | '*', patch: VectorPaintPatch, label = 'Change vector paint') {
    this.objectIds = objectIds === '*' ? '*' : [...objectIds];
    this.patch = structuredClone(patch);
    this.label = label;
  }

  retainedBytes(): number {
    return 160 + JSON.stringify(this.patch).length * 2 + (this.before ? JSON.stringify([...this.before.values()]).length * 2 : 0);
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'vector') return rejected('Vector layer no longer exists.');
    if (layer.locked) return rejected('Vector layer is locked.');
    const objectIds = this.objectIds;
    const targets = objectIds === '*'
      ? layer.vector.objects
      : objectIds.map((id) => layer.vector.objects.find((object) => object.id === id));
    if (targets.some((object) => !object)) return rejected('Vector object no longer exists.');
    const objects = targets as VectorObject[];
    const next = (object: VectorObject): { fill: SolidPaint | null; stroke: VectorStroke | null } => ({
      fill: this.patch.fill === undefined ? object.fill : structuredClone(this.patch.fill),
      stroke: this.patch.stroke === undefined ? object.stroke : structuredClone(this.patch.stroke),
    });
    if (objects.every((object) => JSON.stringify({ fill: object.fill, stroke: object.stroke }) === JSON.stringify(next(object)))) return noOp();
    this.before ??= new Map(objects.map((object) => [object.id, structuredClone({ fill: object.fill, stroke: object.stroke })]));
    for (const object of objects) {
      const value = next(object);
      object.fill = value.fill;
      object.stroke = value.stroke;
    }
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'vector' || !this.before) return;
    for (const object of layer.vector.objects) {
      const value = this.before.get(object.id);
      if (!value) continue;
      object.fill = structuredClone(value.fill);
      object.stroke = structuredClone(value.stroke);
    }
  }

  damage(): DamageSet { return damage({ layerIds: [this.layerId] }); }
}
